'use client';

import React from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useConnectedAccounts } from '@/hooks/useConnectedAccounts';

const GUIDE_PATH = '/letsgo/docs/plans/floai_go_live_obs_guide.md';

/**
 * OBS go-live entry. Twitch Profile connect is paused (`profileConnectComingSoon`) until this
 * path is product-ready; YouTube remains the live OBS destination users can unlock today.
 */
export function GoLiveObsCard() {
  const { data: accounts, isPending } = useConnectedAccounts(true);
  const youtubeConnected = Boolean(accounts?.youtube?.isConnected);
  const twitchConnected = Boolean(accounts?.twitch?.isConnected);
  const hasObsPath = youtubeConnected || twitchConnected;

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Go live with OBS
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          FloAI does not host the live video pipe in v1. Use OBS (or Streamlabs) with your platform
          stream key. Twitch connect returns when this go-live path is fully shipped.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip label={youtubeConnected ? 'YouTube connected' : 'YouTube not connected'} size="small" />
          {twitchConnected ? <Chip label="Twitch connected" size="small" color="success" /> : null}
        </Stack>
        {isPending ? (
          <Typography variant="body2" color="text.secondary">
            Loading connection status...
          </Typography>
        ) : hasObsPath ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Use the guide for stream key steps, OBS settings, and troubleshooting:
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {youtubeConnected ? (
                <Button component="a" href={`${GUIDE_PATH}#youtube-live-obs-path`} variant="contained">
                  YouTube Live steps
                </Button>
              ) : null}
              {twitchConnected ? (
                <Button component="a" href={`${GUIDE_PATH}#twitch-obs-path`} variant="contained">
                  Twitch steps
                </Button>
              ) : null}
              <Button component="a" href={GUIDE_PATH} variant="outlined">
                Full OBS guide
              </Button>
            </Stack>
          </Box>
        ) : (
          <Alert severity="info" variant="outlined">
            Connect YouTube in this profile first to unlock OBS go-live steps. Twitch will return with
            the OBS go-live release.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
